import React from "react";

import {
  TransactionStatus,
  useChainbridge,
} from "../Contexts/ChainbridgeContext";
import { useWeb3 } from "@chainsafe/web3-context";
import StepProgress from "../Components/Custom/StepProgress";
import {
  Alert,
  Heading,
  Progress,
  Button,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  Text,
  Container,
  Box,
  Stack,
  Link,
} from "@chakra-ui/react";
import { WarningTwoIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { BoldText } from "../Components/UI/UIHelpers";

const trackerUrl = process.env.REACT_APP_TRACKER_URL;

interface ITransferActiveModalProps {
  open: boolean;
  close: () => void;
}

const TransferActiveModal: React.FC<ITransferActiveModalProps> = ({
  open,
  close,
}: ITransferActiveModalProps) => {
  const {
    transactionStatus,
    depositVotes,
    relayerThreshold,
    homeChain,
    destinationChain,
    depositAmount,
    transferTxHash,
    selectedToken,
    homeChainIsPalm,
  } = useChainbridge();
  const { address, tokens } = useWeb3();

  const progressStates: Array<TransactionStatus> = [
    "Initializing Transfer",
    "In Transit",
    "Transfer Completed",
  ];

  // If we're on Palm, include paying the bridge fees as the first item
  if (homeChainIsPalm) {
    progressStates.unshift("Paying Bridge Fees");
  }

  const tokenSymbol = selectedToken && tokens[selectedToken]?.symbol;
  return (
    <Drawer
      onClose={close}
      isOpen={open}
      placement="bottom"
      closeOnEsc={false}
      closeOnOverlayClick={false}
    >
      <DrawerOverlay />
      <DrawerContent>
        <Progress
          size="sm"
          isIndeterminate
          bg="linear-gradient(90deg, #E3C20B 0%, #4C286C 31.25%, #995C8D 55.73%, #4C286C 79.69%, #E3C20B 100%)"
        />
        <DrawerBody pt="32px" pb="32px">
          <Container maxW="container.xl">
            <Box width="100%" maxWidth="728px">
              <StepProgress
                states={progressStates}
                currentState={transactionStatus || progressStates[0]}
              />
              {transactionStatus === "Paying Bridge Fees" && (
                <TransactionMessage
                  heading="Approve bridge and carbon credit fees..."
                  body="Approve the wallet transaction to pay the bridge and carbon credit fees. Open your wallet you did not see it pop up."
                />
              )}
              {transactionStatus === "Initializing Transfer" && (
                <TransactionMessage
                  heading="Starting bridge transfer..."
                  body="Approve the wallet transaction to start the bridge transfer. Open your wallet extension if you did not see it pop up."
                />
              )}
              {transactionStatus === "In Transit" && (
                <Box mt="32px">
                  <Heading as="h3" fontSize="24px" mb="32px">
                    Transferring assets (
                    {depositVotes < (relayerThreshold || 0)
                      ? `${depositVotes}/${relayerThreshold} signatures needed`
                      : "verifying transfer"}
                    )...
                  </Heading>
                  <Text>
                    Your assets are being transferred from one network to the
                    other.
                  </Text>
                </Box>
              )}
              {transactionStatus === "Transfer Completed" && (
                <>
                  <Box mt="32px">
                    <Heading as="h3" fontSize="24px" mb="32px">
                      Transfer complete!
                    </Heading>
                    <Text>
                      You successfully transferred{" "}
                      <BoldText>
                        {depositAmount} {tokenSymbol}{" "}
                      </BoldText>
                      from <BoldText>{homeChain?.name}</BoldText>&nbsp;to&nbsp;
                      <BoldText>{destinationChain?.name}</BoldText>.&nbsp;
                      {!homeChainIsPalm && (
                        <span>
                          Also, your PALM balance was topped up so that it
                          equals 0.5 PALM.
                        </span>
                      )}
                    </Text>
                  </Box>
                  <Box as={Stack} spacing="32px" direction={"column"} mt="32px">
                    <Box>
                      {homeChain &&
                        homeChain.blockExplorer &&
                        transferTxHash && (
                          <Button
                            variant="outline"
                            leftIcon={<ExternalLinkIcon />}
                            onClick={() =>
                              destinationChain &&
                              destinationChain.blockExplorer &&
                              transferTxHash &&
                              window.open(
                                `${destinationChain.blockExplorer}/tx/${transferTxHash}`,
                                "_blank"
                              )
                            }
                            disabled={
                              !destinationChain ||
                              !destinationChain.blockExplorer ||
                              !transferTxHash
                            }
                          >
                            View transaction
                          </Button>
                        )}
                    </Box>
                    <Box>
                      <Button variant="outline" onClick={close}>
                        Start new transfer
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
              {transactionStatus === "Transfer Aborted" && (
                <>
                  <TransactionMessage
                    heading="Something went wrong."
                    body="We could not complete your transfer"
                  />
                  <Box mt="32px">
                    <Button variant="outline" onClick={close}>
                      Start new transfer
                    </Button>
                  </Box>
                </>
              )}
              {transactionStatus !== "Transfer Completed" &&
                transactionStatus != "Transfer Aborted" && (
                  <Alert status="warning" bg="#E09400" color="white" mt="78px">
                    <WarningTwoIcon /> &nbsp;
                    <Text>
                      This may take several hours. Please do not refresh or
                      leave the page. If your transfer takes more than several
                      hours, you can{" "}
                      <Link
                        isExternal
                        style={{ textDecoration: "underline" }}
                        href="https://support.palm.io/hc/en-us/requests/new"
                      >
                        contact support
                      </Link>{" "}
                      for more information. Track your{" "}
                      <Link
                        isExternal
                        style={{ textDecoration: "underline" }}
                        href={
                          address
                            ? `${trackerUrl}?address=${address}`
                            : trackerUrl
                        }
                      >
                        active transfers <ExternalLinkIcon />
                      </Link>
                      .
                    </Text>
                  </Alert>
                )}
            </Box>
          </Container>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default TransferActiveModal;

interface ITransactionMessage {
  heading?: string;
  body?: string;
}

const TransactionMessage: React.FC<ITransactionMessage> = ({
  heading,
  body,
}: ITransactionMessage) => {
  return (
    <Box mt="32px">
      <Heading as="h3" fontSize="24px" mb="32px">
        {heading}
      </Heading>
      <Text>{body}</Text>
    </Box>
  );
};
